import React from "react";
import { Alert, Card, Divider, Space, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import SolicitacaoReservaStatusTag, {
  type StatusSolicitacaoReservaFront,
} from "../components/SolicitacaoReservaStatusTag";
import type {
  PagamentoTaxaCheckoutStatus,
  ResumoEstadiaByTokenResponse,
} from "../../../services/solicitacaoReservaService";
import { formatDate } from "../../../uteis/dateUtils";

const { Title, Text, Paragraph } = Typography;

const moeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

type Props = {
  resumo: ResumoEstadiaByTokenResponse | null;
  statusSolicitacao: StatusSolicitacaoReservaFront | null;
  pagamentoTaxaEm?: string | null;
  prazoFinalBusca?: string | null;
  pagamentoStatus?: PagamentoTaxaCheckoutStatus | null;
};

const pagamentoStatusLabel: Record<PagamentoTaxaCheckoutStatus, string> = {
  PENDENTE: "Pendente",
  RECEBIDO: "Recebido",
  VENCIDO: "Vencido",
  CANCELADO: "Cancelado",
};

export default function StepTaxaPagaAcompanhamento({
  resumo,
  statusSolicitacao,
  pagamentoTaxaEm,
  prazoFinalBusca,
  pagamentoStatus = null,
}: Props) {
  const prazoBuscaDiasUteis = 7;
  const prazoFinal = prazoFinalBusca;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div>
            <SolicitacaoReservaStatusTag status={statusSolicitacao} />
          </div>

          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            <Title level={2} style={{ margin: 0 }}>
              Taxa recebida com sucesso
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Sua solicitação foi registrada e já pode seguir para verificação de
              disponibilidade.
            </Text>
          </Space>

          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="Pagamento confirmado"
            description="Recebemos a taxa de verificação. Agora nossa equipe seguirá com o processo de consulta e validação da reserva."
            style={{ borderRadius: 14 }}
          />

          <Card
            bordered={false}
            style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Title level={4} style={{ margin: 0 }}>
                Próximos passos
              </Title>

              <Space align="start">
                <ClockCircleOutlined style={{ marginTop: 4 }} />
                <Text>
                  O prazo de análise e verificação é de até{" "}
                  <strong>{prazoBuscaDiasUteis} dias úteis</strong>.
                </Text>
              </Space>

              {pagamentoTaxaEm && (
                <Space align="start">
                  <CheckCircleOutlined style={{ marginTop: 4 }} />
                  <Text>
                    Data do pagamento da taxa:{" "}
                    <strong>{formatDate(pagamentoTaxaEm)}</strong>.
                  </Text>
                </Space>
              )}

              {prazoFinal && (
                <Space align="start">
                  <ClockCircleOutlined style={{ marginTop: 4 }} />
                  <Text>
                    Prazo final da análise: <strong>{formatDate(prazoFinal)}</strong>.
                  </Text>
                </Space>
              )}

              {!pagamentoTaxaEm && (
                <Space align="start">
                  <InfoCircleOutlined style={{ marginTop: 4 }} />
                  <Text>
                    O prazo começa a contar a partir da confirmação do pagamento da taxa.
                  </Text>
                </Space>
              )}

              <Space align="start">
                <InfoCircleOutlined style={{ marginTop: 4 }} />
                <Text>
                  Assim que houver andamento, confirmação ou necessidade de retorno, você
                  será notificado.
                </Text>
              </Space>

              <Space align="start">
                <CreditCardOutlined style={{ marginTop: 4 }} />
                <Text>
                  O pagamento do saldo restante será solicitado somente após a confirmação
                  da reserva.
                </Text>
              </Space>
            </Space>
          </Card>

          {prazoFinal && (
            <Card
              bordered={false}
              style={{ borderRadius: 14, background: "rgba(24,144,255,0.06)" }}
              styles={{ body: { padding: 18 } }}
            >
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Title level={4} style={{ margin: 0 }}>
                  Prazo da análise
                </Title>

                {pagamentoTaxaEm && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <Text type="secondary">Data do pagamento da taxa</Text>
                    <Text strong>{formatDate(pagamentoTaxaEm)}</Text>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Prazo final</Text>
                  <Text strong>{formatDate(prazoFinal)}</Text>
                </div>
              </Space>
            </Card>
          )}

          {resumo && (
            <Card
              bordered={false}
              style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
              styles={{ body: { padding: 18 } }}
            >
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Title level={4} style={{ margin: 0 }}>
                  Resumo da solicitação
                </Title>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Empreendimento</Text>
                  <Text strong>{resumo.empreendimento?.nome ?? "—"}</Text>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Período</Text>
                  <Text strong>
                    {formatDate(resumo.checkin)} → {formatDate(resumo.checkout)}
                  </Text>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Noites</Text>
                  <Text strong>{resumo.noites ?? "—"}</Text>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Taxa paga</Text>
                  <Text strong>{moeda(resumo.taxaCondicional ?? 0)}</Text>
                </div>

                {pagamentoStatus && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <Text type="secondary">Status do pagamento da taxa</Text>
                    <Text strong>{pagamentoStatusLabel[pagamentoStatus]}</Text>
                  </div>
                )}

                <div style={{ marginTop: 4 }}>
                  <Tag color="blue" style={{ borderRadius: 999 }}>
                    Dados em análise
                  </Tag>
                </div>
              </Space>
            </Card>
          )}

          <Alert
            type="info"
            showIcon
            message="Alterações bloqueadas"
            description="Nesta etapa, a solicitação entra em acompanhamento. Para evitar inconsistências, os dados deixam de ser editáveis."
            style={{ borderRadius: 14 }}
          />

          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Caso seja necessário algum ajuste, o ideal é que ele seja tratado
            operacionalmente, e não pelo formulário.
          </Paragraph>
        </Space>
      </Card>
    </div>
  );
}
